import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Text,
  Stack,
  Heading,
  Badge,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import FeatureSection from '../components/FeatureSection'
import PerformanceSection from '../components/PerformanceSection'
import ResourceSection from '../components/ResourceSection'
import WhySection from '../components/WhySection'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { keyframes } from '@emotion/react'

const scrollWheel = keyframes`
  0% { transform: translateX(-50%) translateY(0); opacity: 1; }
  80% { transform: translateX(-50%) translateY(10px); opacity: 1; }
  81% { transform: translateX(-50%) translateY(0); opacity: 0; }
  100% { transform: translateX(-50%) translateY(0); opacity: 1; }
`

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)

  return (
    <DefaultLayout>
      {/* ===== Hero (100vh 섹션) ===== */}
      <Box
        as="section"
        position="relative"
        minH="100vh"
        bg="dark.bg"
        overflow="hidden"
      >
        {/* 배경 비디오 */}
        <Box
          as="video"
          autoPlay
          loop
          muted
          playsInline
          src="/videos/hero.mp4"
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          objectFit="cover"
          zIndex={0}
          sx={{ filter: 'brightness(0.4)' }}
        />

        {/* 콘텐츠 */}
        <Box
          position="relative"
          zIndex={1}
          maxW="4xl"
          mx="auto"
          px={4}
          py={{ base: 16, md: 24 }}
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
        >
          <Stack spacing={6}>
            <Badge
              colorScheme="cyan"
              alignSelf="center"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="sm"
              mb={4}
              bgGradient="linear(to-r, cyan.500, blue.500)"
              color="white"
              boxShadow="0 0 20px rgba(0, 188, 212, 0.3)"
            >
              {t('AI 기반 자동매매 플랫폼')}
            </Badge>

            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
              fontWeight="extrabold"
              color="white"
              lineHeight="1.2"
            >
              <Text as="span" bgGradient="linear(to-r, cyan.400, blue.500)" bgClip="text">
                AI
              </Text>
              {t('가 분석하고 트레이딩하는')}
              <br />
              <Text as="span" bgGradient="linear(to-r, blue.500, purple.500)" bgClip="text">
                {t('완벽한 자동화')}
              </Text>
            </Heading>

            <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.300" maxW="2xl" mx="auto">
              {t('감정 없는 알고리즘, 24시간 멈추지 않는 수익 창출')}
              <br />
              {t('딥러닝 분석')} • {t('실시간 데이터 처리')} • {t('리스크 관리 AI')}
            </Text>

            <Box textAlign="center">
              <Button
                mt={5}
                size="lg"
                px={8}
                w="200px"
                py={6}
                borderRadius="full"
                bgGradient="linear(to-r, brand.500, brand.300)"
                color="white"
                fontWeight="bold"
                _hover={{
                  bgGradient: 'linear(to-r, brand.600, brand.400)',
                  transform: 'scale(1.03)',
                  transition: '0.2s ease-in-out'
                }}
                onClick={() => {
                  auth.id ? navigate('/trading') : navigate('/signIn')
                }}
                rightIcon={<ArrowForwardIcon />}
              >
                {t('시작하기')}
              </Button>
            </Box>

            {/* Scroll Indicator */}
            <Box mt={12}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
              >
                <Box
                  w="20px"
                  h="30px"
                  border="2px solid white"
                  borderRadius="14px"
                  position="relative"
                >
                  <Box
                    w="4px"
                    h="8px"
                    bg="white"
                    borderRadius="full"
                    position="absolute"
                    top="4px"
                    left="50%"
                    transform="translateX(-50%)"
                    animation={`${scrollWheel} 2.5s infinite ease-in-out`}
                  />
                </Box>
                <Text color="white" fontSize="sm">
                  {t('스크롤하여 더 보기')}
                </Text>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* ===== 섹션 2: 리소스 ===== */}
      <ResourceSection />

      {/* ===== 섹션 3: 왜 Xrplay인가 ===== */}
      <WhySection />

      {/* ===== 섹션 5: 자동매매 성과 & 특징 ===== */}
      <PerformanceSection />
      <FeatureSection />

      {/* Footer는 DefaultLayout 안에서 자동으로 붙음 */}
    </DefaultLayout>
  )
}

export default Home
